import React, { FC, useState, useEffect } from 'react'
import { Box, Text } from '@chakra-ui/core'
import { useRecoilValue, useRecoilState } from 'recoil'
import { AdditionalInfo } from '@engyalo/storefront-components'
import {
  orderSent,
  resetIdentity,
} from '@engyalo/storefront-events'
import { useMutation } from '@apollo/client'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import {
  createOrderVariables,
  createOrder_createOrder as ICreateOrder,
} from '@engyalo/storefront-types/lib/graphql/order'
import { IConfig, IOrder} from '@engyalo/storefront-types/lib/plugins/model'
import { OrderStatus } from '@engyalo/storefront-types/lib/graphql/global-types'
import { ExecutionResult } from 'graphql'
import { Hook, IStorefrontSettings } from '@engyalo/storefront-types'
import format from 'number-format.js'
import { configState, cartState, orderConfigState, orderState } from '../../recoil/atoms'
import { cartStatsState } from '../../recoil/selectors'
import { createOrderMutation } from '../../queries'
import { MinAmountProgress } from '../MinAmountProgress'
import { getFlatProductValues, getProductSummaries } from '../../utils/analytics'
import { ILocationSearch, isInPreviewMode, skipIfInPreviewMode } from '../../utils/mode'
import { usePlugin } from '../../Plugin'
import _ from 'lodash'

export const ButtonTotalCart: FC = () => {
    const { t } = useTranslation()
    const history = useHistory()
    const location = useLocation<ILocationSearch>()
    const { customer, order } = useParams()
    const config = useRecoilValue<IConfig>(configState)
    const { totalNum, totalPrice } = useRecoilValue(cartStatsState)
    const [globalOrder, setGlobalOrder] = useRecoilState<IOrder>(orderState)
    const storefrontPlugin = usePlugin(Hook.STOREFRONT)
    const [minAmout, setMinAmout] = useState<number | undefined>(undefined)
    const [cart, setCart] = useRecoilState(cartState)
    const cartIsEmpty = _.isEmpty(cart);
    const [canPlaceOrder, setCanPlaceOrder] = useState<boolean>((!minAmout || Number(totalPrice) >= minAmout) && !cartIsEmpty)
    const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
    const storefrontSettings = storefrontPlugin?.props as IStorefrontSettings

    let hidePrices: boolean
    if ((storefrontPlugin?.props as IStorefrontSettings)?.hidePrices == undefined) {
      hidePrices = totalPrice <= 0
    } else {
      hidePrices = (storefrontPlugin?.props as IStorefrontSettings)?.hidePrices ?? false
    }

    useEffect(() => {
        const { metadata } = orderConfig
    
        if (metadata) {
          try {
            const jsonMetadata = JSON.parse(metadata)
    
            const { client } = jsonMetadata
            if (client) {
              const { minAmount } = client
              setMinAmout(Number.parseFloat(minAmount))
              setCanPlaceOrder((!Number.parseFloat(minAmount) || Number(totalPrice) >= Number.parseFloat(minAmount)) && !cartIsEmpty)
            }else{
              setMinAmout(config.minAmount)
              setCanPlaceOrder((!config.minAmount || Number(totalPrice) >= config.minAmount) && !cartIsEmpty)
            }
          } catch {
            setMinAmout(config.minAmount)
            setCanPlaceOrder((!config.minAmount || Number(totalPrice) >= config.minAmount) && !cartIsEmpty)
          }
        } else {
          setMinAmout(config.minAmount)
          setCanPlaceOrder((!config.minAmount || Number(totalPrice) >= config.minAmount) && !cartIsEmpty)
        }

      }, [config, orderConfig, totalPrice, cartIsEmpty])

    const session = {
        userID: orderConfig.userID || '',
        customer: config.customer,
        sessionID: orderConfig.id || '',
    }

    const onTextChange = (e: React.FormEvent<HTMLInputElement>): void => {
        setGlobalOrder({
          ...globalOrder,
          orderNote: e.currentTarget.value,
        })
    }

    const [createOrder, { loading: loadingOrderCreation, called: orderCreated }] = useMutation<
        ICreateOrder,
        createOrderVariables
    >(createOrderMutation, {
        variables: {
        order: {
            storefrontID: config.storefrontId,
            userID: orderConfig.userID || '',
            status: OrderStatus.ENABLED,
            provider: orderConfig.channelName,
            total: Number(totalPrice.toFixed(2)),
            totalQty: totalNum,
            cart: Object.values(cart),
            note: globalOrder.orderNote,
        },
        configId: order,
        },
        onCompleted: () => {
        const getCartItemValues = getFlatProductValues(cart)

        skipIfInPreviewMode(() => {
            orderSent({
            cart: {
                size: totalNum,
                value: Number(totalPrice),
                ids: getCartItemValues('id'),
                skus: getCartItemValues('sku'),
                names: getCartItemValues('name'),
                categories: getCartItemValues('category'),
                note: globalOrder.orderNote,
                product_summaries: getProductSummaries(cart)
            },
            storefront: {
                currency: config.currency,
            },
            session,
            })
        })

        resetIdentity()

        if (storefrontSettings?.onOrderCreated) {
            storefrontSettings.onOrderCreated({ cart, orderConfig, t })
            return
        }
        if (orderConfig.channelName === 'whatsapp') {
            window.location.href = `http://wa.me/${orderConfig.channelID}`
        }
        },
    })

    return (
        <Box
            paddingY="1.2rem"
            paddingX="0.8rem"
            boxShadow="md"
            position="sticky"
            zIndex={2}
            background="white"
            width="100%"
            maxW="42.5rem"
            bottom={0}
            flex="0 0 auto"
        >
            {totalNum > 0 && config.enabledOrderNote && (
            // TODO agregar un padding
            <AdditionalInfo
                title={t('cart-observations')}
                subtext={t('cart-observationsAlter')}
                value={globalOrder.orderNote}
                onChange={onTextChange}
                inputProps={{ placeholder: t('cart-observationsPlaceholder') }}
            />
            )}
            {!canPlaceOrder && (
            <>
                {minAmout && (
                <MinAmountProgress
                    currencyPrefix={config.currencyPrefix}
                    currentAmount={Number(totalPrice)}
                    minAmount={minAmout}
                />
                )}
            </>
            )}
            <Box
            display="flex"
            width="100%"
            paddingY="1.2rem"
            paddingX="1.6rem"
            backgroundColor="primary.500"
            opacity={loadingOrderCreation || orderCreated ? 0.5 : 'initial'}
            borderColor="transparent"
            fontWeight="500"
            color="white"
            fontFamily="body"
            fontSize="1.1rem"
            borderRadius="0.4rem"
            justifyContent={hidePrices ? 'center' : 'space-between'}
            pointerEvents={loadingOrderCreation || orderCreated ? 'none' : 'auto'}
            cursor="pointer"
            onClick={(): boolean | Promise<ExecutionResult<ICreateOrder>> => {
                if (loadingOrderCreation || orderCreated) {
                return false
                }

                if (!canPlaceOrder) {
                history.push({ pathname: `/${customer}/${order}`, search: location.search })
                return false
                }

                if (isInPreviewMode(location)) {
                return false
                }

                return createOrder()
            }}
            >
            {(canPlaceOrder) ? (
                <>
                <Text fontSize="lg" alignSelf="center">
                    {t('cart-pay')}
                </Text>
                {!hidePrices && (
                    <Text fontSize="lg" alignSelf="center">
                    {config.currencyPrefix} {format(config.currencyFormat, totalPrice)}
                    </Text>
                )}
                </>
            ) : (
                <Text fontSize="lg" textAlign="center" flex="1">
                {t('cart-goToCatalogue')}
                </Text>
            )}
            </Box>
        </Box>
    )
}